/* eslint-disable @typescript-eslint/no-unused-vars */
import { useState, useMemo, useEffect, SetStateAction } from 'react'
import { RouteComponentProps } from 'react-router-dom'
import { Column, Cell, Row } from 'react-table'
import { generatePath, useHistory } from 'react-router-dom'
import type { HomeId } from '@racepointenergy/swc/scs/types'
import { stateEntityAdapter } from '@racepointenergy/swc/scs/features/host-states'
import { dealerMonitorStates } from '@racepointenergy/swc/scs/dealer-monitor-states'
import { useIntl, FormattedMessage } from 'react-intl'
import Icon from '@mdi/react'
import { mdiArrowLeft } from '@mdi/js'

import { homeActiveConfigEnergySelector } from 'features/home/selectors'
import { useAppSelector, useAppDispatch } from 'app/hooks'
import ROUTES from 'app/routes'
import {
  fetchHomeActiveConfigEnergyThunk,
  homeActiveConfigEnergyActions,
} from 'features/home/components/HomeEnergySystem/slice'
import { Table, TableLoader } from 'components/Table'
import { messages } from '../messages'
import {
  Bold,
  PanelTableWrapper,
  TableLeft,
  TableRight,
  TableLeftRow,
  TableRightRow,
  RadioTilesWrapper,
  NavBack,
  NameSpan,
} from '../styles'
import { Panel, SmartHardware, Circuit, Load } from 'features/home/types'
import theme from 'theme_'
import { RadioTiles } from 'components/RadioTiles'
import PanelData from './PanelData'
import { currentRootDomainIdSelector } from 'features/rootDomains/selectors'
import BreakerBlock from './BreakerBlock'
import { ChannelCell } from './ChannelCell'

const hostStateInitialState = stateEntityAdapter.getInitialState()

interface PanelInfoProps
  extends RouteComponentProps<{ homeId: HomeId; panelId: string }> {}

function PanelInfo({ match }: PanelInfoProps) {
  const { formatMessage } = useIntl()
  const dispatch = useAppDispatch()
  const history = useHistory()

  const [panelData, setPanelData] = useState<
    (Panel & { backup: string }) | null
  >(null)
  const [isDesktop, setIsDesktop] = useState<boolean>(true)
  const [isMobile, setIsMobile] = useState<boolean>(false)
  const [breakers, setBreakers] = useState<any[]>([])
  const [loads, setLoads] = useState<Load[]>([])
  const [isPduThree, setIsPduThree] = useState<boolean>(false)
  const [panelSide, setPanelSide] = useState('left')

  const { homeId, panelId } = match.params

  const currentRootId = useAppSelector(currentRootDomainIdSelector)
  const isHomeActiveConfigEnergyLoaded = useAppSelector(
    homeActiveConfigEnergySelector.isFinished
  )
  const homeActiveConfigEnergy = useAppSelector(
    homeActiveConfigEnergySelector.data
  )

  const handleResize = () => {
    setIsDesktop(window.matchMedia(theme.devices.desktop).matches)
    setIsMobile(window.matchMedia(theme.devices.mobile).matches)
  }

  const {
    data = hostStateInitialState,
    isError,
    isFetching,
    error,
  } = dealerMonitorStates.useQuery({ id: homeId })

  useEffect(() => {
    if (homeId && !isHomeActiveConfigEnergyLoaded) {
      dispatch(fetchHomeActiveConfigEnergyThunk(homeId))
    }
  }, [dispatch, homeId, isHomeActiveConfigEnergyLoaded])

  useEffect(() => {
    if (!homeActiveConfigEnergy) return

    let l = []
    if (panelData?.mac.length) {
      const pduThree = homeActiveConfigEnergy.lightingDevices?.find(
        (device) =>
          device.uid === panelData?.mac[0] && device.type === 'PDUENGYM31'
      )

      if (pduThree) {
        setIsPduThree(true)
      }

      if (pduThree?.loads) {
        l = pduThree?.loads.map((load) => {
          const circuitName =
            homeActiveConfigEnergy.energyConfig?.circuits?.find(
              (circuit) =>
                circuit.companion?.includes(load?.loadID) ||
                circuit.load === load?.loadID
            )?.name

          return { ...load, circuitName }
        })
        setLoads(l)
      }
    }

    const smartHardware = homeActiveConfigEnergy.lightingDevices?.flatMap(
      (device) => {
        return device.smartHardware.filter(
          (hardware) => hardware.olaConfig.PanelID === panelId
        )
      }
    )

    const dumbHardware =
      homeActiveConfigEnergy.energyConfig?.panelConfig?.breakerPanelModules?.filter(
        (device) => device.breakerPanelId === panelId
      )

    const hwWithChannel = smartHardware?.map((hw) => {
      const circuitName = homeActiveConfigEnergy.energyConfig?.circuits?.find(
        (circuit) => circuit.companion?.includes(hw.hwUID)
      )?.name

      const status =
        data.entities[`Energy.Circuit.${circuitName}.Status`]?.value

      if (status) {
        if ((status & 0b100) === 1) {
          return {
            ...hw,
            status: 2,
          }
        } else if ((status & 0b1) === 1) {
          return {
            ...hw,
            status: 1,
          }
        }

        return {
          ...hw,
          status: 0,
        }
      }

      return { ...hw, status: -1 }
    })

    const b = Array(panelData?.slotsNumber || 0)
      .fill(null)
      .map((e, i) => ({
        name: '',
        olaConfig: { SlotNumber: i + 1, DevicesModelDescription: '' },
      })) as any[]

    hwWithChannel?.forEach((hardware) => {
      const circuit = homeActiveConfigEnergy.energyConfig?.circuits?.find(
        (circuit) => circuit.companion === hardware.smartHardwareID
      )
      b[hardware.olaConfig.SlotNumber - 1] = {
        ...hardware,
        ...circuit,
      }

      if (
        (hardware.olaConfig.DeviceModelID === 2 ||
          hardware.olaConfig.DeviceModelID === 6) &&
        panelData?.panelType
      ) {
        b[
          panelData?.slidesNumber === 'Dual'
            ? hardware.olaConfig.SlotNumber + 1
            : hardware.olaConfig.SlotNumber
        ] = {
          ...hardware,
          ...circuit,
          olaConfig: {
            ...hardware.olaConfig,
            SlotNumber:
              panelData?.slidesNumber === 'Dual'
                ? hardware.olaConfig.SlotNumber + 2
                : hardware.olaConfig.SlotNumber + 1,
          },
        }
      }

      if (
        parseInt(hardware.hwUID, 16) % 2 === 0 ||
        hardware.olaConfig.DeviceModelID !== 8
      )
        return

      let slots = 3
      if (
        panelData?.panelType === 'Cutler-Hammer Type BR' ||
        panelData?.panelType === 'Square D QO' ||
        panelData?.panelType === 'Square D'
      ) {
        slots = 4
      }

      if (panelData?.slidesNumber === 'Dual' && panelData?.panelType) {
        if (hardware.olaConfig.DeviceModelID === 8) {
          b[hardware.olaConfig.SlotNumber + 1] = {
            ...circuit,
            ...hardware,
            olaConfig: {
              ...hardware.olaConfig,
              SlotNumber: hardware.olaConfig.SlotNumber + 2,
            },
          }
          b[hardware.olaConfig.SlotNumber + 3] = {
            ...circuit,
            ...hardware,
            olaConfig: {
              ...hardware.olaConfig,
              SlotNumber: hardware.olaConfig.SlotNumber + 4,
            },
          }

          if (slots === 4) {
            b[hardware.olaConfig.SlotNumber + 5] = {
              ...circuit,
              ...hardware,
              olaConfig: {
                ...hardware.olaConfig,
                SlotNumber: hardware.olaConfig.SlotNumber + 6,
              },
            }
          }
        }
      } else if (panelData?.slidesNumber === 'Single' && panelData?.panelType) {
        b[hardware.olaConfig.SlotNumber + 1] = {
          ...circuit,
          ...hardware,
          olaConfig: {
            ...hardware.olaConfig,
            SlotNumber: hardware.olaConfig.SlotNumber + 2,
          },
        }
        b[hardware.olaConfig.SlotNumber] = {
          ...circuit,
          ...hardware,
          olaConfig: {
            ...hardware.olaConfig,
            SlotNumber: hardware.olaConfig.SlotNumber + 1,
          },
        }
        if (hardware.olaConfig.DeviceModelID === 8) {
          b[hardware.olaConfig.SlotNumber + 1] = {
            ...circuit,
            ...hardware,
            olaConfig: {
              ...hardware.olaConfig,
              SlotNumber: hardware.olaConfig.SlotNumber + 2,
            },
          }
          if (slots === 4) {
            b[hardware.olaConfig.SlotNumber + 2] = {
              ...circuit,
              ...hardware,
              olaConfig: {
                ...hardware.olaConfig,
                SlotNumber: hardware.olaConfig.SlotNumber + 3,
              },
            }
          }
        }
      }
    })

    dumbHardware?.forEach((d) => {
      let circuitName = ''
      const ch =
        homeActiveConfigEnergy.energyConfig?.panelConfig?.breakerPanelModules?.find(
          (device) =>
            device.channels?.find((channel) => channel?.circuitInId === d.id)
        )

      if (ch?.channels?.length) {
        const uid = ch.channels.find(
          (channel) => channel?.circuitInId === d.id
        )?.uid

        if (uid) {
          const hwid = homeActiveConfigEnergy?.lightingDevices
            ?.find((device) =>
              device.smartHardware.find((hw) => hw.hwUID === uid)
            )
            ?.smartHardware.find((hw) => hw.hwUID === uid)?.smartHardwareID

          if (hwid) {
            const cName = homeActiveConfigEnergy?.groups?.find((group) =>
              group.smartHardwareIDs.includes(hwid)
            )?.name

            if (cName) {
              circuitName = cName
            }
          }
        }
      }
      d.slots.forEach((slot) => {
        if (b[slot - 1]?.name?.length === 0) {
          b[slot - 1] = {
            olaConfig: { SlotNumber: slot, DevicesModelDescription: d.type },
            name: circuitName,
            id: d.id,
          }
        }
      })
    })

    setBreakers(b ?? [])
  }, [homeActiveConfigEnergy, panelData, panelId, data.entities])

  useEffect(() => {
    if (
      homeActiveConfigEnergy?.energyConfig?.panelConfig?.breakerPanels?.length
    ) {
      const e =
        homeActiveConfigEnergy.energyConfig?.panelConfig.breakerPanels.find(
          (panel) => panel.id === panelId
        )
      if (e) {
        const backup = homeActiveConfigEnergy.energyConfig?.controllers
          .filter((controller) => controller.microgridConfig)
          .find((controller) => controller.breakerPanels?.includes(panelId))
        setPanelData({ ...e, backup: backup?.name ?? '-' })
      }
    }
  }, [homeActiveConfigEnergy, setPanelData, panelId])

  useEffect(() => {
    window.addEventListener('resize', handleResize)
    handleResize()

    return () => {
      window.removeEventListener('resize', handleResize)
      dispatch(homeActiveConfigEnergyActions.clear())
    }
  }, [dispatch, homeId])

  const softwareUpdatesTiles = [
    {
      label: formatMessage(messages.left),
      value: 'left',
      defaultChecked: true,
    },
    {
      label: formatMessage(messages.right),
      value: 'right',
    },
  ]

  const headers = useMemo(
    () => ({
      slot: {
        label: formatMessage(messages.headerSlotNumber),
        id: 'Slot',
      },
      type: {
        label: formatMessage(messages.headerDeviceType),
        id: 'Type',
      },
      channel: {
        label: formatMessage(messages.headerChannel),
        id: 'Channel',
      },
      circuit: {
        label: formatMessage(messages.headerCircuitName),
        id: 'Circuit',
      },
    }),
    [formatMessage]
  )

  const pduThreeHeaders = useMemo(
    () => ({
      slot: {
        label: formatMessage(messages.headerSlotNumber),
        id: 'Slot',
      },
      circuit: {
        label: formatMessage(messages.headerCircuitName),
        id: 'Circuit',
      },
    }),
    [formatMessage]
  )

  const columns: readonly Column<SmartHardware & Circuit>[] = useMemo(
    () => [
      {
        id: headers.slot.id,
        Header: <NameSpan>{headers.slot.label}</NameSpan>,
        accessor: (row) => row.olaConfig.SlotNumber,
        Cell: (cell: Cell<SmartHardware & Circuit>) => (
          <NameSpan>{cell.value}</NameSpan>
        ),
        percentWidth: '19%',
      },
      {
        id: headers.type.id,
        Header: headers.type.label,
        accessor: (row) => row.olaConfig.DevicesModelDescription,
        percentWidth: '30%',
      },
      {
        id: headers.channel.id,
        Header: headers.channel.label,
        accessor: (row) => row.status,
        percentWidth: '21%',
        Cell: (cell: Cell<SmartHardware & Circuit>) =>
          ChannelCell(cell, panelData?.slidesNumber === 'Dual'),
      },
      {
        id: headers.circuit.id,
        Header: headers.circuit.label,
        accessor: (row) => row.name,
        percentWidth: '30%',
      },
    ],
    [headers, panelData?.slidesNumber]
  )

  const pduThreeColumns: readonly Column<Load>[] = useMemo(
    () => [
      {
        id: headers.slot.id,
        Header: <NameSpan>{headers.slot.label}</NameSpan>,
        accessor: (row) => row.offset + 1,
        Cell: (cell: Cell<SmartHardware & Circuit>) => (
          <NameSpan>{cell.value}</NameSpan>
        ),
        percentWidth: '30%',
      },
      {
        id: headers.circuit.id,
        Header: headers.circuit.label,
        accessor: (row) => row.circuitName,
        percentWidth: '70%',
      },
    ],
    [headers]
  )

  const onRowClick = (row: Row<SmartHardware & Circuit>) => {
    if (row?.original?.hwUID) {
      const breakerPanelPath = generatePath(ROUTES.module, {
        rootDomainId: currentRootId || '',
        homeId: homeId || ' ',
        panelId: panelId,
        moduleId: row?.original?.hwUID,
      })

      return history.push(breakerPanelPath)
    } else if (row?.original?.id) {
      const breakerPanelPath = generatePath(ROUTES.breaker, {
        rootDomainId: currentRootId || '',
        homeId: homeId || ' ',
        panelId: panelId,
        breakerId: row?.original?.id,
      })

      return history.push(breakerPanelPath)
    }
  }

  const onBackClick = () => {
    const devicesPath = generatePath(ROUTES.breakerPanels, {
      rootDomainId: currentRootId || '',
      homeId: homeId || ' ',
    })

    return history.push(devicesPath)
  }

  const tables = () => {
    if (isPduThree) {
      return (
        <Table
          columns={pduThreeColumns}
          data={loads}
          isPaginated={false}
          mobileViewComponent={BreakerBlock}
        />
      )
    }

    if (isMobile) {
      return (
        <Table
          columns={columns}
          data={breakers}
          onRowClick={onRowClick}
          isPaginated={false}
          mobileViewComponent={BreakerBlock}
        />
      )
    }

    if (panelData?.slidesNumber === 'Dual') {
      const filteredEven = breakers.filter((e, index) => {
        return index % 2 === 0
      })
      const filteredOdd = breakers.filter((e, index) => {
        return index % 2 !== 0
      })

      return (
        <>
          {isDesktop ? (
            <PanelTableWrapper>
              <Table
                columns={columns}
                data={filteredEven}
                isPaginated={false}
                tableStyles={TableLeft}
                rowStyles={TableLeftRow}
                onRowClick={onRowClick}
              />
              <Table
                columns={columns}
                data={filteredOdd}
                isPaginated={false}
                tableStyles={TableRight}
                rowStyles={TableRightRow}
                onRowClick={onRowClick}
              />
            </PanelTableWrapper>
          ) : (
            <>
              <RadioTilesWrapper>
                <RadioTiles
                  options={softwareUpdatesTiles}
                  name="updateTileFilter"
                  onChange={setPanelSide}
                />
              </RadioTilesWrapper>

              {panelSide === 'left' ? (
                <Table
                  columns={columns}
                  data={filteredEven}
                  isPaginated={false}
                  tableStyles={TableLeft}
                  rowStyles={TableLeftRow}
                  onRowClick={onRowClick}
                />
              ) : (
                <Table
                  columns={columns}
                  data={filteredOdd}
                  isPaginated={false}
                  tableStyles={TableRight}
                  rowStyles={TableRightRow}
                  onRowClick={onRowClick}
                />
              )}
            </>
          )}
        </>
      )
    }

    return (
      <Table
        columns={columns}
        data={breakers}
        onRowClick={onRowClick}
        isPaginated={false}
      />
    )
  }

  return (
    <>
      <NavBack onClick={onBackClick}>
        <Icon path={mdiArrowLeft} size={1} />
        <FormattedMessage {...messages.backToPanels} />
      </NavBack>

      <PanelData
        panelData={panelData}
        loaded={isHomeActiveConfigEnergyLoaded}
        isDesktop={isDesktop}
      />

      {!isHomeActiveConfigEnergyLoaded && <TableLoader />}

      {isHomeActiveConfigEnergyLoaded && tables()}
    </>
  )
}

export default PanelInfo
